import { Platform } from 'react-native'

// `pb` is imported lazily (not at module top level) to break the require cycle
// pocketbase → errors → notify → … → web-push → pocketbase. web-push only needs
// pb at call time inside these async actions, so a dynamic import is free here
// and matches the same cycle-breaking pattern used in pocketbase.ts.
//
// This is an imperative ServiceWorker/PushManager utility (not a React hook), so
// useMutation/pbtsdb primitives can't apply — the whole file is exempted from the
// pbtsdb-no-raw-pb-access plugin in biome.json.

export function isPushSupported(): boolean {
    return (
        Platform.OS === 'web' &&
        typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window
    )
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!isPushSupported()) return null
    return navigator.serviceWorker.register('/sw.js')
}

export async function subscribeToPush(userId: string): Promise<boolean> {
    if (!isPushSupported()) return false

    const registration = await registerServiceWorker()
    if (!registration) return false

    const publicKey = process.env.VITE_VAPID_PUBLIC_KEY || process.env.VAPID_PUBLIC_KEY
    if (!publicKey) return false

    const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    })

    const subscriptionJSON = subscription.toJSON()

    const { pb } = await import('./pocketbase')
    await pb.collection('push_subscriptions').create({
        user: userId,
        endpoint: subscriptionJSON.endpoint,
        keys: {
            p256dh: subscriptionJSON.keys?.p256dh,
            auth: subscriptionJSON.keys?.auth,
        },
        user_agent: navigator.userAgent.slice(0, 500),
        platform: 'web',
    })

    return true
}

// `authToken` is passed explicitly (rather than read from pb.authStore) so the
// caller can run teardown as part of logout without racing pb.authStore.clear():
// when provided it's sent as the Authorization header so the delete is
// authorized even once the store has been cleared. The in-app settings toggle
// omits it and relies on the live session.
export async function unsubscribeFromPush(userId: string, authToken?: string): Promise<void> {
    if (!isPushSupported()) return

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
        await subscription.unsubscribe()

        const authOptions = authToken ? { headers: { Authorization: authToken } } : {}
        const { pb } = await import('./pocketbase')
        const records = await pb.collection('push_subscriptions').getFullList({
            ...authOptions,
            filter: pb.filter('user = {:userId} && endpoint = {:endpoint}', {
                userId,
                endpoint: subscription.endpoint,
            }),
        })
        for (const record of records) {
            await pb.collection('push_subscriptions').delete(record.id, authOptions)
        }
    }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
}
